import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Table,
  Dropdown,
  Segmented,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Tag,
  Space,
  Tooltip,
  Empty,
  Typography,
} from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  ExclamationCircleFilled,
  ReloadOutlined,
  TableOutlined,
  AppstoreOutlined,
  NumberOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../../variables/constants";
import SideBar from "../../../../components/SideBar";
import NavBar from "../../../../components/NavBar";
import Footer from "../../../../components/Footer";
import AddValeurPointIPV2, { trancheLabel } from "./AddValeurPointIPV2";

const { confirm } = Modal;
const { Title, Text } = Typography;

const ACCENT = "#0891b2";

const fmtFCFA = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  return Number.isFinite(n) ? `${n.toLocaleString("fr-FR")} FCFA` : String(val);
};

const normalize = (arr) => [...(arr || []).map(Number)].filter(Number.isFinite).sort((a, b) => a - b);
const keyOf = (arr) => JSON.stringify(normalize(arr));

const ValeurPointIPV2 = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grille");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [preset, setPreset] = useState({ taux: null, age: null });

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${TOKEN}`, "Content-Type": "application/json" }),
    []
  );

  const sortByIdDesc = (arr) =>
    [...arr].sort((a, b) =>
      a.valeurPointIpId != null && b.valeurPointIpId != null ? b.valeurPointIpId - a.valeurPointIpId : 0
    );

  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/valeur-point-ip`, { headers })
      .then((res) => setData(sortByIdDesc(res.data || [])))
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des configurations.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- actions ---
  const openCreate = (presetTaux = null, presetAge = null) => {
    setEditMode(false);
    setSelectedItem(null);
    setPreset({ taux: presetTaux, age: presetAge });
    setModalOpen(true);
  };
  const openEdit = (item) => {
    setEditMode(true);
    setSelectedItem(item);
    setPreset({ taux: null, age: null });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setSelectedItem(null);
    setPreset({ taux: null, age: null });
  };

  const onAdd = (created) => setData((prev) => sortByIdDesc([created, ...prev]));
  const onUpdate = (updated) =>
    setData((prev) => sortByIdDesc(prev.map((it) => (it.valeurPointIpId === updated.valeurPointIpId ? updated : it))));

  const confirmDelete = (id) =>
    confirm({
      title: "Supprimer cette configuration ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/api/valeur-point-ip/delete/${id}`, { headers });
          setData((prev) => prev.filter((it) => it.valeurPointIpId !== id));
          toast.success("Supprimé avec succès.");
        } catch (err) {
          console.error(err);
          toast.error("Erreur lors de la suppression.");
        }
      },
    });

  // --- stats ---
  const stats = useMemo(() => {
    const vals = data.map((d) => Number(d.valeur)).filter(Number.isFinite);
    const allTaux = data.flatMap((d) => normalize(d.trancheTauxIp));
    const allAge = data.flatMap((d) => normalize(d.trancheAgeBlesse));
    const avg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
    return {
      count: data.length,
      min: vals.length ? Math.min(...vals) : 0,
      max: vals.length ? Math.max(...vals) : 0,
      avg,
      tauxRange: allTaux.length ? `${Math.min(...allTaux)} – ${Math.max(...allTaux)} %` : "—",
      ageRange: allAge.length ? `${Math.min(...allAge)} – ${Math.max(...allAge)} ans` : "—",
    };
  }, [data]);

  // --- filtre recherche ---
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((d) => {
      const s = `${trancheLabel(d.trancheTauxIp, " %")} ${trancheLabel(d.trancheAgeBlesse, " ans")} ${d.valeur}`.toLowerCase();
      return s.includes(q);
    });
  }, [data, search]);

  // --- pivot pour la grille ---
  const pivot = useMemo(() => {
    const rowMap = new Map(); // key taux -> arr
    const colMap = new Map(); // key age -> arr
    const cellMap = new Map(); // `${rowKey}|${colKey}` -> record
    filtered.forEach((d) => {
      const rk = keyOf(d.trancheTauxIp);
      const ck = keyOf(d.trancheAgeBlesse);
      if (!rowMap.has(rk)) rowMap.set(rk, normalize(d.trancheTauxIp));
      if (!colMap.has(ck)) colMap.set(ck, normalize(d.trancheAgeBlesse));
      cellMap.set(`${rk}|${ck}`, d);
    });
    const firstOf = (arr) => (arr.length ? arr[0] : Number.MAX_SAFE_INTEGER);
    const rows = [...rowMap.entries()].sort((a, b) => firstOf(a[1]) - firstOf(b[1]));
    const cols = [...colMap.entries()].sort((a, b) => firstOf(a[1]) - firstOf(b[1]));
    return { rows, cols, cellMap };
  }, [filtered]);

  const heat = (val) => {
    const { min, max } = stats;
    if (!Number.isFinite(val)) return "transparent";
    const ratio = max > min ? (val - min) / (max - min) : 0.5;
    return `rgba(8, 145, 178, ${0.08 + ratio * 0.55})`;
  };

  // --- colonnes liste ---
  const columns = [
    {
      title: "Tranche taux IP",
      dataIndex: "trancheTauxIp",
      key: "trancheTauxIp",
      render: (arr) => <Tag color="cyan">{trancheLabel(arr, " %")}</Tag>,
      sorter: (a, b) => (normalize(a.trancheTauxIp)[0] || 0) - (normalize(b.trancheTauxIp)[0] || 0),
    },
    {
      title: "Tranche âge blessé",
      dataIndex: "trancheAgeBlesse",
      key: "trancheAgeBlesse",
      render: (arr) => <Tag color="geekblue">{trancheLabel(arr, " ans")}</Tag>,
      sorter: (a, b) => (normalize(a.trancheAgeBlesse)[0] || 0) - (normalize(b.trancheAgeBlesse)[0] || 0),
    },
    {
      title: "Valeur du point IP",
      dataIndex: "valeur",
      key: "valeur",
      align: "right",
      render: (val) => <Text strong>{fmtFCFA(val)}</Text>,
      sorter: (a, b) => Number(a.valeur || 0) - Number(b.valeur || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              { key: "edit", label: "Modifier", onClick: () => openEdit(record) },
              { key: "delete", label: "Supprimer", danger: true, onClick: () => confirmDelete(record.valeurPointIpId) },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const kpi = (title, value, suffix) => (
    <Card size="small" bordered style={{ borderRadius: 12 }}>
      <Statistic
        title={<Text type="secondary">{title}</Text>}
        value={value}
        suffix={suffix}
        valueStyle={{ color: ACCENT, fontWeight: 600, fontSize: 20 }}
        groupSeparator=" "
      />
    </Card>
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* En-tête */}
              <Card
                bordered={false}
                style={{ borderRadius: 14, marginBottom: 16, background: "linear-gradient(135deg,#ecfeff,#f8fafc)" }}
                bodyStyle={{ padding: 20 }}
              >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                  <Col>
                    <Space align="center" size={14}>
                      <div
                        style={{
                          width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center",
                          background: ACCENT, color: "#fff", fontSize: 22,
                        }}
                      >
                        <NumberOutlined />
                      </div>
                      <div>
                        <Title level={4} style={{ margin: 0 }}>Valeur du point IP</Title>
                        <Text type="secondary">Barème d'indemnisation de l'incapacité permanente (IPP)</Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Space wrap>
                      <Button icon={<ReloadOutlined />} onClick={fetchAll}>Actualiser</Button>
                      <Button onClick={() => navigate("/parametres/ajouter-valeur-point-ip")}>Vue classique</Button>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()} style={{ background: ACCENT }}>
                        Ajouter
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* KPIs */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} md={8} lg={4}>{kpi("Barèmes", stats.count)}</Col>
                <Col xs={12} md={8} lg={5}>{kpi("Valeur moyenne", stats.avg, " FCFA")}</Col>
                <Col xs={12} md={8} lg={5}>{kpi("Valeur min", stats.min, " FCFA")}</Col>
                <Col xs={12} md={8} lg={5}>{kpi("Valeur max", stats.max, " FCFA")}</Col>
                <Col xs={24} md={12} lg={5}>
                  <Card size="small" bordered style={{ borderRadius: 12 }}>
                    <Text type="secondary" style={{ display: "block" }}>Plages couvertes</Text>
                    <Space direction="vertical" size={0}>
                      <Text><Tag color="cyan" style={{ marginRight: 4 }}>IP</Tag>{stats.tauxRange}</Text>
                      <Text><Tag color="geekblue" style={{ marginRight: 4 }}>Âge</Tag>{stats.ageRange}</Text>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {/* Barre d'outils */}
              <Card bordered style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
                <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  <Col>
                    <Segmented
                      value={view}
                      onChange={setView}
                      options={[
                        { label: "Grille", value: "grille", icon: <AppstoreOutlined /> },
                        { label: "Liste", value: "liste", icon: <TableOutlined /> },
                      ]}
                    />
                  </Col>
                  <Col>
                    <Input
                      allowClear
                      prefix={<SearchOutlined />}
                      placeholder="Rechercher une tranche ou une valeur…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 300, maxWidth: "100%" }}
                    />
                  </Col>
                </Row>

                {view === "liste" ? (
                  <Table
                    rowKey={(r) => r.valeurPointIpId}
                    dataSource={filtered}
                    columns={columns}
                    loading={loading}
                    pagination={{ pageSize: 10, showSizeChanger: true, hideOnSinglePage: true }}
                    size="middle"
                  />
                ) : pivot.rows.length === 0 ? (
                  <Empty description="Aucun barème" style={{ padding: 40 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()} style={{ background: ACCENT }}>
                      Ajouter un barème
                    </Button>
                  </Empty>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="vpip-grid" style={{ borderCollapse: "separate", borderSpacing: 6, minWidth: 520 }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              position: "sticky", left: 0, zIndex: 2, background: "#fff",
                              textAlign: "left", padding: "8px 12px", color: "#64748b", fontWeight: 600,
                            }}
                          >
                            Taux IP ＼ Âge
                          </th>
                          {pivot.cols.map(([ck, arr]) => (
                            <th key={ck} style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                              <Tag color="geekblue" style={{ margin: 0 }}>{trancheLabel(arr, " ans")}</Tag>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pivot.rows.map(([rk, rArr]) => (
                          <tr key={rk}>
                            <td
                              style={{
                                position: "sticky", left: 0, zIndex: 1, background: "#fff",
                                padding: "8px 12px", whiteSpace: "nowrap",
                              }}
                            >
                              <Tag color="cyan" style={{ margin: 0 }}>{trancheLabel(rArr, " %")}</Tag>
                            </td>
                            {pivot.cols.map(([ck, cArr]) => {
                              const rec = pivot.cellMap.get(`${rk}|${ck}`);
                              const val = rec ? Number(rec.valeur) : null;
                              return (
                                <td key={ck} style={{ padding: 0 }}>
                                  {rec ? (
                                    <Tooltip title="Cliquer pour modifier">
                                      <div
                                        onClick={() => openEdit(rec)}
                                        style={{
                                          cursor: "pointer", borderRadius: 10, padding: "12px 14px",
                                          textAlign: "right", fontWeight: 600, color: "#0f172a",
                                          background: heat(val), border: "1px solid rgba(8,145,178,0.15)",
                                          minWidth: 120, transition: "transform .08s",
                                        }}
                                      >
                                        {val.toLocaleString("fr-FR")}
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Définir cette combinaison">
                                      <div
                                        onClick={() => openCreate(rArr, cArr)}
                                        style={{
                                          cursor: "pointer", borderRadius: 10, padding: "12px 14px",
                                          textAlign: "center", color: "#cbd5e1",
                                          border: "1px dashed #e2e8f0", minWidth: 120,
                                        }}
                                      >
                                        +
                                      </div>
                                    </Tooltip>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                      Intensité de couleur proportionnelle à la valeur du point. Cliquez une cellule pour la modifier,
                      une case vide pour la définir.
                    </Text>
                  </div>
                )}
              </Card>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddValeurPointIPV2
        open={modalOpen}
        onClose={closeModal}
        editMode={editMode}
        selectedItem={selectedItem}
        items={data}
        presetTaux={preset.taux}
        presetAge={preset.age}
        onAdd={onAdd}
        onUpdate={onUpdate}
      />
    </div>
  );
};

export default ValeurPointIPV2;
